import React, { useContext } from 'react'
import styled, { ThemeContext } from 'styled-components'
import useFarmingPools from '../../hooks/useFarmingPools'
import PoolCard from './PoolCard'
import { useActiveWeb3React } from '../../hooks'
import { ButtonPrimary } from '../../components/Button'
import { useWalletModalToggle } from '../../state/application/hooks'
import IntroCard from 'components/IntroCard'
import { TYPE } from 'theme'
import { LoadingRows } from 'pages/Pool/styleds'
import { AutoColumn } from 'components/Column'
import { RowBetween } from 'components/Row'
import { Helmet } from 'react-helmet'

const ContainerStyled = styled.div`
  width: 100%;
  max-width: 870px;
  margin-left: auto;
  margin-right: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 200px;
  padding: 10px;
`

const GridStyled = styled.div`
  display: grid;
  gap: 18px;
  grid-template-columns: 1fr;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

const ConnectWalletContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: auto;
  min-height: 25vh;
  background: ${({ theme }) => theme.bg0};
  padding: 8px;
  border-radius: 14px;
  width: 100%;

  div {
    max-width: 300px;
  }
`
const TitleRow = styled(RowBetween)`
  color: ${({ theme }) => theme.text2};
  ${({ theme }) => theme.mediaWidth.upToSmall`
    flex-wrap: wrap;
    gap: 12px;
    width: 100%;
    flex-direction: column-reverse;
  `};
`

const MainContentWrapper = styled.main`
  background-color: ${({ theme }) => theme.bg0};
  padding: 18px;
  border-radius: 14px;
  display: flex;
  flex-direction: column;
`

const FarmList: React.FC = () => {
  const { account } = useActiveWeb3React()
  const toggleWalletModal = useWalletModalToggle()
  const { pools, loading } = useFarmingPools()
  const theme = useContext(ThemeContext)

  return (
    <>
      <Helmet>
        <title>Farm | HyperSwap Exchange on Hyperliquid</title>
        <meta
          name="description"
          content="Stake your LP tokens in farm pools on HyperSwap Exchange. Earn high rewards securely on Hyperliquid's EVM blockchain."
        />
        <meta
          name="keywords"
          content="HyperSwap farm pools, staking rewards, Hyperliquid blockchain, DeFi farming, earn crypto, Hyperliquid EVM"
        />
        <meta property="og:title" content="Farm Pools | HyperSwap Exchange on Hyperliquid" />
        <meta
          property="og:description"
          content="Earn rewards by staking in farm pools on HyperSwap Exchange, running on the Hyperliquid blockchain."
        />
        <meta property="og:url" content={`${window.location.origin}/#/farm`} />
      </Helmet>
      <ContainerStyled>
        <IntroCard variant={1} Key="Farm list">
          <h1>Farm</h1>
          <p>
            Welcome to the Farm page, where you can deposit your V2-LP tokens to participate in exclusive farming pools
            and maximize your crypto earnings. Only V2-LP tokens are eligible for deposits.
          </p>
        </IntroCard>

        <AutoColumn gap="lg" justify="center">
          <AutoColumn gap="lg" style={{ width: '100%' }}>
            <TitleRow style={{ marginTop: '1rem' }} padding={'0'}>
              <TYPE.mediumHeader>{'V2 Farming Pools Overview'}</TYPE.mediumHeader>
            </TitleRow>
            {!account ? (
              <ConnectWalletContainer>
                <div>
                  <TYPE.mediumHeader textAlign="center" color={theme.text3}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="48"
                      height="48"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="icon icon-tabler icons-tabler-outline icon-tabler-tractor"
                      style={{ marginBottom: '.5rem' }}
                    >
                      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                      <path d="M7 15m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0" />
                      <path d="M7 15l0 .01" />
                      <path d="M19 17m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
                      <path d="M10.5 17l6.5 0" />
                      <path d="M20 15.2v-4.2a1 1 0 0 0 -1 -1h-6l-2 -5h-6v6.5" />
                      <path d="M18 5h-1a1 1 0 0 0 -1 1v4" />
                    </svg>

                    <div>{'Connect your wallet to see the available farming pools.'}</div>
                  </TYPE.mediumHeader>
                  <ButtonPrimary style={{ marginTop: '2em', padding: '8px 16px' }} onClick={toggleWalletModal}>
                    Connect a wallet
                  </ButtonPrimary>
                </div>
              </ConnectWalletContainer>
            ) : (
              <MainContentWrapper>
                {loading ? (
                  <LoadingRows>
                    <div />
                    <div />
                    <div />
                    <div />
                    <div />
                    <div />
                    <div />
                    <div />
                    <div />
                    <div />
                    <div />
                    <div />
                  </LoadingRows>
                ) : (
                  <>
                    {pools.length > 0 ? (
                      <GridStyled>
                        {pools.map((pool: any, index: number) => {
                          return <PoolCard pool={pool} pId={index} key={index} />
                        })}
                      </GridStyled>
                    ) : (
                      <p style={{ textAlign: 'center' }}>No active pools found.</p>
                    )}
                  </>
                )}
              </MainContentWrapper>
            )}
          </AutoColumn>
        </AutoColumn>
      </ContainerStyled>
    </>
  )
}

export default FarmList
